import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getUnidades, getAtributosCatalogo, getProyectosUnidades, setUnidadAtributo,
  createColumna, updateColumna, deleteColumna, exportUnidadesExcel, renombrarOpcion,
  createUnidad, deleteUnidad,
} from '../../api/client'
import PlanillaFiltros from './PlanillaFiltros'
import { aplicarFiltros, columnasFiltrables, filtroCompleto } from './filtrosLogica'
import { idealDe, desviaciones, COLUMNA_GRUPO } from './idealLogica'
import TableFilters from '../TableFilters'
import PlanillaCell from './PlanillaCells'
import { AddColumnaModal, ColumnaMenu, EncabezadoFijo } from './PlanillaColumnas'
import { ordenar, siguienteOrden } from './ordenLogica'
import { TIPO_ICON, moverClave } from './tipos'
import ArchivosModal from './ArchivosModal'
import { usePuedeEditarMantenimiento } from '../../utils/mantenimiento'

// The unit grid for one project: fixed fields plus one column per catalog
// column (GET /api/atributos/catalogo), each edited by its type's cell.
// Columns are added, retyped and deleted from here -- by those who may edit
// Mantenimiento; everyone else sees it read-only (sort, filter, export).
export default function Planilla({ estados, estadoLabel, onOpenUnidad }) {
  const { t } = useTranslation('engineering')
  const puedeEditar = usePuedeEditarMantenimiento()
  const [proyectos, setProyectos] = useState([])
  const [params, setParams] = useState({})
  const [rows, setRows] = useState([])
  const [columnas, setColumnas] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [cellError, setCellError] = useState('')
  const [adding, setAdding] = useState(false)
  const [menu, setMenu] = useState(null)          // { columna, anchor }
  const [archivosDe, setArchivosDe] = useState(null)   // { unidad, columna }
  // Column filters, applied here over the loaded project (filtrosLogica.js).
  const [filtros, setFiltros] = useState([])
  const [verFiltros, setVerFiltros] = useState(false)
  // Ids of the units ticked for the Excel download.
  const [seleccion, setSeleccion] = useState(() => new Set())
  const [exportando, setExportando] = useState(false)
  // The project's "bus ideal" rows (one per group), pinned above the buses,
  // and whether each bus is measured against its group's ideal.
  const [ideales, setIdeales] = useState([])
  const [verIdeal, setVerIdeal] = useState(true)
  const [comparar, setComparar] = useState(false)
  // Row order, by one column (ordenLogica.js); null = load order. Ideals stay on top.
  const [orden, setOrden] = useState(null)
  const ordenarPor = (campo, dir) => setOrden(o => siguienteOrden(o, campo, dir))
  const requestId = useRef(0)

  // The project the Planilla opens on: the one with the most units, so it
  // never opens blank waiting for a pick nobody knows is needed. null until
  // known -- the filters render only then, so they start with it chosen.
  const [inicial, setInicial] = useState(null)

  // Catalog projects too, not only the units' -- otherwise a project with no
  // units yet can't even be picked.
  useEffect(() => {
    Promise.all([getProyectosUnidades(), getUnidades()])
      .then(([ps, unidades]) => {
        setProyectos(ps)
        const count = {}
        unidades.forEach(u => { if (u.proyecto) count[u.proyecto.toLowerCase()] = (count[u.proyecto.toLowerCase()] || 0) + 1 })
        const best = [...ps].sort((a, b) => (count[b.toLowerCase()] || 0) - (count[a.toLowerCase()] || 0))[0]
        setInicial(best ? { proyecto: best } : {})
      })
      .catch(() => setInicial({}))
  }, [])

  const load = async (p) => {
    const id = ++requestId.current
    setCellError('')
    setMenu(null)
    if (!p.proyecto) { setRows([]); setColumnas([]); setIdeales([]); return }
    setLoading(true)
    try {
      const [unidades, catalogo, idealesP] = await Promise.all([
        getUnidades({ ...p, incluir_atributos: true }),
        getAtributosCatalogo(p.proyecto),
        // Ideals ignore the estado/search filters: they are the yardstick.
        getUnidades({ proyecto: p.proyecto, ideales: 'solo', incluir_atributos: true }),
      ])
      if (id !== requestId.current) return
      setRows(unidades)
      setColumnas(catalogo)
      setIdeales(idealesP)
      setLoadError('')
    } catch {
      if (id === requestId.current) setLoadError(t('unidades.planilla.loadFailed'))
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }

  const onFiltersChange = (p) => {
    // Another project has other columns: its filters and ticks don't carry over.
    if ((p.proyecto || '') !== (params.proyecto || '')) { setFiltros([]); setSeleccion(new Set()) }
    setParams(p)
    load(p)
  }

  // Each bus with the columns where it strays from its group's ideal
  // (_desv) and how many (desviaciones -- a filterable number).
  const rowsCmp = useMemo(() => (comparar
    ? rows.map(u => { const d = desviaciones(u, idealDe(u, ideales), columnas); return { ...u, _desv: d, desviaciones: d.length } })
    : rows), [rows, ideales, columnas, comparar])

  const filtrables = useMemo(() => columnasFiltrables(columnas, {
    codigo: t('unidades.codigo'), desviaciones: t('unidades.ideal.deviations'),
  }, comparar ? [{ id: 'desviaciones', tipo: 'numero' }] : []), [columnas, t, comparar])
  const visibles = useMemo(() => aplicarFiltros(rowsCmp, filtros, filtrables), [rowsCmp, filtros, filtrables])
  const ordenadas = useMemo(() => ordenar(visibles, orden,
    [...filtrables, { id: 'estado_actual', tipo: 'texto', fijo: true }]), [visibles, orden, filtrables])
  const filtrosActivos = filtros.filter(filtroCompleto).length

  // The header box ticks/unticks every VISIBLE unit; hidden ticks are kept.
  const todasVisibles = visibles.length > 0 && visibles.every(u => seleccion.has(u.id))
  const algunaVisible = visibles.some(u => seleccion.has(u.id))
  const toggleTodas = () => setSeleccion(s => {
    const next = new Set(s)
    visibles.forEach(u => todasVisibles ? next.delete(u.id) : next.add(u.id))
    return next
  })
  const toggleUna = (id) => setSeleccion(s => {
    const next = new Set(s)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  // Ticks of units that no longer exist (deleted, other project) never count.
  // Ticked ideals go in the Excel too (on top).
  const seleccionadas = [...ideales, ...rows].filter(u => seleccion.has(u.id))

  const descargar = async () => {
    setExportando(true)
    setCellError('')
    try { await exportUnidadesExcel(seleccionadas.map(u => u.id), params.proyecto) }
    catch { setCellError(t('unidades.filtros.exportFailed')) }
    setExportando(false)
  }

  // A bus or an ideal: both lists hold units edited the same way.
  const patchRow = (unidadId, fn) => {
    setRows(rs => rs.map(r => r.id === unidadId ? fn(r) : r))
    setIdeales(is => is.map(r => r.id === unidadId ? fn(r) : r))
  }
  const patchTodas = (fn) => { setRows(rs => rs.map(fn)); setIdeales(is => is.map(fn)) }

  // "+ Bus ideal": a new ideal for a group, named after it. Its Grupo cell is
  // set too (adding the group as an option if the column is an estado one),
  // since that is what ties buses to it.
  const agregarIdeal = async () => {
    const nombre = (prompt(t('unidades.ideal.newPrompt')) || '').trim()
    if (!nombre) return
    setCellError('')
    try {
      const u = await createUnidad({ codigo: `IDEAL · ${nombre}`, proyecto: params.proyecto,
                                     estado_actual: 'Operativo', es_ideal: true })
      let atributos = {}
      const grupoCol = columnas.find(c => c.nombre === COLUMNA_GRUPO)
      if (grupoCol) {
        const valor = grupoCol.tipo === 'estado' ? await addOpcion(grupoCol, nombre) : nombre
        if (valor) {
          await setUnidadAtributo(u.id, grupoCol.nombre, valor)
          atributos = { [grupoCol.nombre]: valor }
        }
      }
      setIdeales(is => [...is, { ...u, atributos, archivos: {} }])
      setVerIdeal(true)
    } catch (err) {
      const d = err.response?.data?.detail
      setCellError(err.response?.status === 409
        ? t('unidades.codigoDuplicate', { codigo: `IDEAL · ${nombre}` })
        : d?.code === 'proyecto_desconocido' ? (d.message || t('unidades.proyectoDesconocido'))
        : t('unidades.saveFailed'))
    }
  }

  const eliminarIdeal = async (ideal) => {
    if (!confirm(t('unidades.ideal.deleteConfirm', { codigo: ideal.codigo }))) return
    try {
      await deleteUnidad(ideal.id)
      setIdeales(is => is.filter(i => i.id !== ideal.id))
    } catch {
      setCellError(t('unidades.deleteFailed'))
    }
  }

  const saveCell = async (unidad, columna, valor) => {
    try {
      const saved = await setUnidadAtributo(unidad.id, columna.nombre, valor)
      patchRow(unidad.id, r => ({ ...r, atributos: { ...(r.atributos || {}), [columna.nombre]: saved.valor } }))
      setCellError('')
    } catch (e) {
      setCellError(t('unidades.planilla.saveFailed', { codigo: unidad.codigo, nombre: columna.nombre }))
      throw e
    }
  }

  const replaceColumna = (c) => setColumnas(cs => cs.map(x => x.id === c.id ? c : x))

  // From an estado cell's "+ Nueva opción…": grow the column's list, return
  // the option to pick (or null if it failed).
  const addOpcion = async (columna, nombre) => {
    const existing = (columna.opciones || []).find(o => o.toLowerCase() === nombre.toLowerCase())
    if (existing) return existing
    try {
      replaceColumna(await updateColumna(columna.id, { opciones: [...(columna.opciones || []), nombre] }))
      return nombre
    } catch {
      setCellError(t('unidades.planilla.columnSaveFailed'))
      return null
    }
  }

  const onCreateColumna = async (data) => {
    const c = await createColumna(data)
    setColumnas(cs => [...cs, c])
  }

  const onUpdateColumna = async (columna, data) => {
    const c = await updateColumna(columna.id, data)
    replaceColumna(c)
    setMenu(m => m && { ...m, columna: c })
    // Renamed: the backend moved the saved values; move the loaded ones too.
    if (c.nombre !== columna.nombre) patchTodas(r => moverClave(r, columna.nombre, c.nombre))
  }

  // Rename an estado option; the backend changes the saved values too, and
  // here the loaded rows and any filter on that value follow.
  const onRenombrarOpcion = async (columna, de, a, fusionar) => {
    const { columna: c } = await renombrarOpcion(columna.id, de, a, fusionar)
    replaceColumna(c)
    setMenu(m => m && { ...m, columna: c })
    const final = fusionar ? c.opciones.find(o => o.toLowerCase() === a.toLowerCase()) : a.trim()
    patchTodas(r => r.atributos?.[columna.nombre] === de
      ? { ...r, atributos: { ...r.atributos, [columna.nombre]: final } } : r)
    setFiltros(fs => fs.map(f => String(f.campo) === String(columna.id) && f.valor === de ? { ...f, valor: final } : f))
  }

  const onDeleteColumna = async (columna) => {
    const alcance = t('unidades.planilla.deleteScopeProject', { proyecto: columna.proyecto })
    if (!confirm(t('unidades.planilla.deleteColumnConfirm', { nombre: columna.nombre, alcance }))) return
    try {
      await deleteColumna(columna.id)
      setMenu(null)
      setColumnas(cs => cs.filter(x => x.id !== columna.id))
      setFiltros(fs => fs.filter(f => String(f.campo) !== String(columna.id)))
      patchTodas(r => {
        const atributos = { ...(r.atributos || {}) }
        const archivos = { ...(r.archivos || {}) }
        delete atributos[columna.nombre]; delete archivos[columna.nombre]
        return { ...r, atributos, archivos }
      })
    } catch {
      setCellError(t('unidades.planilla.columnSaveFailed'))
    }
  }

  const th = 'text-left px-3 py-2.5 font-semibold text-gray-600 whitespace-nowrap'

  return (
    <div>
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        {inicial === null ? (
          <div className="text-gray-400 text-sm py-2">{t('unidades.loading')}</div>
        ) : (
          <TableFilters
            initial={inicial}
            search={{ param: 'buscar', placeholder: t('unidades.searchPlaceholder') }}
            filters={[
              { param: 'proyecto', label: t('unidades.proyecto'), type: 'select', options: proyectos },
              { param: 'estado_actual', label: t('unidades.estado'), type: 'select',
                options: estados.map(e => ({ value: e.value, label: estadoLabel(e.value) })) },
            ]}
            onChange={onFiltersChange}
          />
        )}
        {params.proyecto && puedeEditar && (
          <button type="button" onClick={() => setAdding(true)}
            className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors shrink-0">
            {t('unidades.planilla.addColumn')}
          </button>
        )}
      </div>

      {params.proyecto && rows.length > 0 && (
        <div className="mb-3 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => {
                  // Opening with nothing yet starts a first filter row.
                  if (!verFiltros && filtros.length === 0 && filtrables.length) {
                    const c = filtrables[0]
                    setFiltros([{ id: Date.now(), campo: c.id, op: 'contains', valor: '' }])
                  }
                  setVerFiltros(v => !v)
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                  filtrosActivos ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
                {t('unidades.filtros.button')}{filtrosActivos ? ` · ${filtrosActivos}` : ''}
              </button>
              <span className="w-px h-5 bg-gray-200" />
              <button type="button" onClick={() => setVerIdeal(v => !v)} disabled={!ideales.length}
                title={ideales.length ? t('unidades.ideal.toggleHint') : t('unidades.ideal.none')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-40 ${
                  verIdeal && ideales.length ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
                ★ {t('unidades.ideal.button')}{ideales.length ? ` · ${ideales.length}` : ''}
              </button>
              <button type="button" onClick={() => setComparar(v => !v)} disabled={!ideales.length}
                title={t('unidades.ideal.compareHint')}
                className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-40 ${
                  comparar ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
                {t('unidades.ideal.compare')}
              </button>
              {puedeEditar && (
                <button type="button" onClick={agregarIdeal} className="text-xs font-medium text-gray-500 hover:text-gray-900">
                  {t('unidades.ideal.add')}
                </button>
              )}
              {filtrosActivos > 0 && (
                <>
                  <span className="text-xs text-gray-500">
                    {t('unidades.filtros.showing', { n: visibles.length, total: rows.length })}
                  </span>
                  <button type="button" onClick={() => setFiltros([])}
                    className="text-xs font-medium text-red-600 hover:text-red-700">
                    {t('unidades.filtros.clearAll')}
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              {seleccionadas.length > 0 && (
                <>
                  <span className="text-xs text-gray-500">{t('unidades.filtros.selected', { count: seleccionadas.length })}</span>
                  <button type="button" onClick={() => setSeleccion(new Set())}
                    className="text-xs font-medium text-gray-500 hover:text-gray-900">
                    {t('unidades.filtros.clearSelection')}
                  </button>
                </>
              )}
              <button type="button" onClick={descargar} disabled={!seleccionadas.length || exportando}
                title={seleccionadas.length ? undefined : t('unidades.filtros.selectFirst')}
                className="inline-flex items-center gap-1.5 bg-gray-900 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40 transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {exportando ? t('unidades.filtros.exporting') : t('unidades.filtros.export')}
              </button>
            </div>
          </div>
          {verFiltros && (
            <PlanillaFiltros columnas={filtrables} filtros={filtros} onChange={setFiltros} />
          )}
        </div>
      )}

      {cellError && <p className="text-red-500 text-xs mb-3">{cellError}</p>}

      {!params.proyecto ? (
        <div className="text-center py-12 text-gray-400"><p>{t('unidades.planilla.pickProject')}</p></div>
      ) : loadError ? (
        <div className="text-red-500 text-sm">{loadError}</div>
      ) : loading && rows.length === 0 ? (
        <div className="text-gray-400 text-sm">{t('unidades.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p>{t('unidades.planilla.noUnits', { proyecto: params.proyecto })}</p>
        </div>
      ) : (
        <div className={`bg-white rounded-xl border border-gray-200 overflow-x-auto transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <table className="text-sm min-w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="w-10 min-w-[2.5rem] px-3 sticky left-0 bg-gray-50 z-10">
                  <input type="checkbox" checked={todasVisibles} aria-label={t('unidades.filtros.selectAll')}
                    title={t('unidades.filtros.selectAll')}
                    ref={el => { if (el) el.indeterminate = !todasVisibles && algunaVisible }}
                    onChange={toggleTodas} className="cursor-pointer" />
                </th>
                <th className={`${th} sticky left-10 bg-gray-50 z-10`}><EncabezadoFijo label={t('unidades.codigo')} campo="codigo" orden={orden} onOrdenar={ordenarPor} /></th>
                <th className={th}><EncabezadoFijo label={t('unidades.estado')} campo="estado_actual" orden={orden} onOrdenar={ordenarPor} /></th>
                {comparar && <th className={th} title={t('unidades.ideal.deviationsHint')}><EncabezadoFijo label={t('unidades.ideal.deviationsShort')} campo="desviaciones" tipo="numero" orden={orden} onOrdenar={ordenarPor} /></th>}
                {columnas.map(c => (
                  <th key={c.id} className="px-1 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">
                    <button type="button" data-columna-btn
                      onClick={e => {
                        const r = e.currentTarget.getBoundingClientRect()
                        setMenu(m => m?.columna.id === c.id ? null : { columna: c, anchor: r })
                      }}
                      title={t(`unidades.planilla.tipos.${c.tipo}`)}
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${
                        menu?.columna.id === c.id ? 'bg-gray-200 text-gray-900' : 'hover:bg-gray-100'}`}>
                      <span className="text-[11px] text-gray-400 font-normal w-4 text-center">{TIPO_ICON[c.tipo]}</span>
                      <span>{c.nombre}</span>
                      {orden && String(orden.campo) === String(c.id) && <span className="text-[10px] text-gray-500 font-normal">{orden.dir === 'asc' ? '▲' : '▼'}</span>}
                      <span className="text-gray-300 font-normal">⋮</span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {verIdeal && ideales.map(u => {
                const marcada = seleccion.has(u.id)
                const fondo = 'bg-amber-50'
                return (
                  <tr key={`ideal-${u.id}`} className="border-b border-amber-200 bg-amber-50">
                    <td className={`w-10 min-w-[2.5rem] px-3 sticky left-0 z-10 ${fondo}`}>
                      <input type="checkbox" checked={marcada} onChange={() => toggleUna(u.id)}
                        aria-label={u.codigo} className="cursor-pointer" />
                    </td>
                    <td className={`px-3 py-2 font-semibold text-amber-900 whitespace-nowrap sticky left-10 z-10 ${fondo}`}>
                      <span className="inline-flex items-center gap-1.5">
                        <button type="button" onClick={() => onOpenUnidad?.(u.id)} title={t('unidades.ideal.rowHint')}
                          className="hover:underline">★ {u.codigo.replace(/^IDEAL · /, '')}</button>
                        {puedeEditar && (
                          <button type="button" onClick={() => eliminarIdeal(u)} title={t('unidades.ideal.delete')}
                            className="text-amber-400 hover:text-red-600 font-normal leading-none">×</button>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-amber-700 whitespace-nowrap" colSpan={2}>{t('unidades.ideal.label')}</td>
                    {comparar && <td />}
                    {columnas.map(c => (
                      <td key={c.id} className="px-1 py-1">
                        <PlanillaCell columna={c}
                          value={u.atributos?.[c.nombre] ?? null}
                          archivos={u.archivos?.[c.nombre] || []}
                          onSave={valor => saveCell(u, c, valor)}
                          onAddOpcion={nombre => addOpcion(c, nombre)}
                          onOpenArchivos={() => setArchivosDe({ unidadId: u.id, columna: c })}
                          soloLectura={!puedeEditar} />
                      </td>
                    ))}
                  </tr>
                )
              })}
              {visibles.length === 0 && (
                <tr><td colSpan={columnas.length + (comparar ? 5 : 4)} className="px-3 py-10 text-center text-gray-400">
                  {t('unidades.filtros.noMatches')}{' '}
                  <button type="button" onClick={() => setFiltros([])} className="underline hover:text-gray-700">
                    {t('unidades.filtros.clearAll')}
                  </button>
                </td></tr>
              )}
              {ordenadas.map(u => {
                const estado = estados.find(e => e.value === u.estado_actual)
                const marcada = seleccion.has(u.id)
                const fondo = marcada ? 'bg-blue-50' : 'bg-white'
                return (
                  <tr key={u.id} className={`border-b border-gray-50 last:border-b-0 ${marcada ? 'bg-blue-50' : 'hover:bg-gray-50/50'}`}>
                    <td className={`w-10 min-w-[2.5rem] px-3 sticky left-0 z-10 ${fondo}`}>
                      <input type="checkbox" checked={marcada} onChange={() => toggleUna(u.id)}
                        aria-label={u.codigo} className="cursor-pointer" />
                    </td>
                    <td className={`px-3 py-2 font-medium text-gray-900 whitespace-nowrap sticky left-10 z-10 ${fondo}`}>
                      <button type="button" onClick={() => onOpenUnidad?.(u.id)} title={t('unidades.panel.open')}
                        className="hover:underline">{u.codigo}</button>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${estado?.cls || 'bg-gray-100 text-gray-600'}`}>
                        {estadoLabel(u.estado_actual)}
                      </span>
                    </td>
                    {comparar && (
                      <td className="px-3 py-2 text-center">
                        {!idealDe(u, ideales) ? <span className="text-xs text-gray-300" title={t('unidades.ideal.noIdealForGroup')}>—</span>
                          : u.desviaciones === 0
                            ? <span className="text-green-600 text-sm" title={t('unidades.ideal.matches')}>✓</span>
                            : <span className="inline-flex min-w-[1.5rem] justify-center px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold tabular-nums"
                                title={u._desv.join(', ')}>{u.desviaciones}</span>}
                      </td>
                    )}
                    {columnas.map(c => {
                      const desvia = comparar && u._desv?.includes(c.nombre)
                      const ideal = desvia ? idealDe(u, ideales) : null
                      return (
                      <td key={c.id} className={`px-1 py-1 ${desvia ? 'bg-rose-50 shadow-[inset_0_0_0_1px_rgb(253,164,175)]' : ''}`}
                        title={desvia ? t('unidades.ideal.cellHint', { valor: ideal.atributos?.[c.nombre] }) : undefined}>
                        <PlanillaCell columna={c}
                          value={u.atributos?.[c.nombre] ?? null}
                          archivos={u.archivos?.[c.nombre] || []}
                          onSave={valor => saveCell(u, c, valor)}
                          onAddOpcion={nombre => addOpcion(c, nombre)}
                          onOpenArchivos={() => setArchivosDe({ unidadId: u.id, columna: c })}
                          soloLectura={!puedeEditar} />
                      </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {params.proyecto && rows.length > 0 && puedeEditar && (
        <p className="text-xs text-gray-400 mt-2">{t('unidades.planilla.hint')}</p>
      )}

      {menu && (
        <ColumnaMenu key={menu.columna.id} columna={menu.columna} anchor={menu.anchor}
          valoresEnUso={[...new Set([...ideales, ...rows].map(r => r.atributos?.[menu.columna.nombre]).filter(Boolean))]}
          onClose={() => setMenu(null)}
          onUpdate={data => onUpdateColumna(menu.columna, data)}
          onRenombrar={(de, a, fusionar) => onRenombrarOpcion(menu.columna, de, a, fusionar)}
          orden={orden} onOrdenar={ordenarPor}
          onDelete={() => onDeleteColumna(menu.columna)}
          soloLectura={!puedeEditar} />
      )}
      {adding && (
        <AddColumnaModal proyecto={params.proyecto} onClose={() => setAdding(false)} onCreate={onCreateColumna} />
      )}
      {archivosDe && (() => {
        const unidad = [...ideales, ...rows].find(r => r.id === archivosDe.unidadId)
        if (!unidad) return null
        const nombre = archivosDe.columna.nombre
        return (
          <ArchivosModal unidad={unidad} columna={archivosDe.columna}
            archivos={unidad.archivos?.[nombre] || []}
            onChange={next => patchRow(unidad.id, r => ({ ...r, archivos: { ...(r.archivos || {}), [nombre]: next } }))}
            onClose={() => setArchivosDe(null)}
            soloLectura={!puedeEditar} />
        )
      })()}
    </div>
  )
}
